import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { Info } from "lucide-react";

import { UIActionModal, UIButton, UICallout } from "../../../../ui";

const isSameId = (first, second) =>
  first !== undefined &&
  first !== null &&
  second !== undefined &&
  second !== null &&
  String(first) === String(second);

const getAssignedZone = (row, zoneOptions = []) => {
  if (row.zone || row.zoneId) return row.zone;
  if (!row.isSupervisor) return "";

  return (
    zoneOptions.find((option) => isSameId(option.supervisorId, row.userId))
      ?.name || ""
  );
};

const getAssignedZoneId = (row, zoneOptions = []) => {
  if (row.zoneId != null && row.zoneId !== "") {
    return String(row.zoneId);
  }

  if (!row.isSupervisor) return null;

  const zone = zoneOptions.find((option) =>
    isSameId(option.supervisorId, row.userId),
  );

  return zone?.id != null ? String(zone.id) : null;
};

const willClearAnyZone = (assignedApplicants, zoneOptions) => {
  const unassignCountByZoneId = assignedApplicants.reduce((counts, row) => {
    const zoneId = getAssignedZoneId(row, zoneOptions);
    if (!zoneId) return counts;

    counts[zoneId] = (counts[zoneId] || 0) + 1;
    return counts;
  }, {});

  return Object.entries(unassignCountByZoneId).some(
    ([zoneId, unassignCount]) => {
      const zoneOption = zoneOptions.find((option) =>
        isSameId(option.id, zoneId),
      );
      const applicantsInZone = zoneOption?.applicantsInZone;

      if (applicantsInZone == null) return false;

      return applicantsInZone - unassignCount <= 0;
    },
  );
};

const isApplicantAssigned = (row, zoneOptions) =>
  Boolean(row.zone || row.zoneId || getAssignedZone(row, zoneOptions));

const getApplicantDescription = (row, copy, zoneOptions) => {
  const id = row.userId || row.requestId || row.id;
  const zone = getAssignedZone(row, zoneOptions);
  const pieces = [`#${id}`];

  if (zone) pieces.push(zone);
  if (row.requestedRole) pieces.push(row.requestedRole);

  return pieces.join(" · ") || copy.assignedStatus;
};

const UnassignRequestModal = ({
  open,
  applicants,
  zoneOptions,
  copy,
  onClose,
  onUnassign,
}) => {
  const [step, setStep] = useState("confirm");

  useEffect(() => {
    if (!open) return;
    setStep("confirm");
  }, [open]);

  const assignedApplicants = useMemo(
    () => applicants.filter((row) => isApplicantAssigned(row, zoneOptions)),
    [applicants, zoneOptions],
  );
  const unassignedApplicants = useMemo(
    () => applicants.filter((row) => !isApplicantAssigned(row, zoneOptions)),
    [applicants, zoneOptions],
  );
  const supervisorApplicants = useMemo(
    () => applicants.filter((row) => row.isSupervisor),
    [applicants],
  );
  const hasOnlyUnassigned =
    applicants.length > 0 && assignedApplicants.length === 0;
  const hasMixedSupervisors =
    supervisorApplicants.length > 0 &&
    supervisorApplicants.length < applicants.length &&
    step !== "supervisorWarning";
  const isBulk = applicants.length > 1;
  const primaryApplicant = applicants[0];
  const shouldShowNothingToUnassign = hasOnlyUnassigned;
  const shouldShowZoneClearedWarning = useMemo(
    () => willClearAnyZone(assignedApplicants, zoneOptions),
    [assignedApplicants, zoneOptions],
  );

  const handleConfirm = () => {
    if (hasMixedSupervisors) {
      setStep("supervisorWarning");
      return;
    }

    onUnassign(assignedApplicants.map((row) => row.id));
  };

  if (shouldShowNothingToUnassign) {
    return (
      <UIActionModal
        open={open}
        onClose={onClose}
        title={copy.nothingTitle}
        showIcon={false}
        maxWidth="sm"
        PaperProps={{ className: "event-applicants-unassign-modal" }}
        actions={
          <Box className="event-applicants-unassign-modal__single-action">
            <UIButton variant="outlined" color="inherit" onClick={onClose}>
              {copy.ok}
            </UIButton>
          </Box>
        }
      >
        <Typography className="event-applicants-unassign-modal__message">
          {copy.nothingBody}
        </Typography>
        <UIActionModal.Divider />
        <Typography className="event-applicants-unassign-modal__muted-message">
          {copy.nothingDetails.replace("{count}", applicants.length)}
        </Typography>
      </UIActionModal>
    );
  }

  const title =
    step === "supervisorWarning" ? copy.supervisorTitle : copy.confirmTitle;
  const intro =
    step === "supervisorWarning"
      ? copy.supervisorBody
          .replace("{count}", applicants.length)
          .replace("{supervisors}", supervisorApplicants.length)
      : isBulk
      ? copy.bulkQuestion.replace("{count}", assignedApplicants.length)
      : copy.singleQuestion;

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={title}
      variant="reject"
      maxWidth="sm"
      PaperProps={{ className: "event-applicants-unassign-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={copy.cancel}
          confirmLabel={copy.unassign}
          confirmVariant="reject"
          confirmDisabled={assignedApplicants.length === 0}
          onCancel={onClose}
          onConfirm={handleConfirm}
        />
      }
    >
      <Typography className="event-applicants-unassign-modal__message">
        {copy.clearZoneHint}
      </Typography>
      <UIActionModal.Divider />
      <Typography className="event-applicants-unassign-modal__question">
        {intro}
      </Typography>

      {!isBulk && primaryApplicant ? (
        <UIActionModal.UserInfo
          name={`${primaryApplicant.name}${
            primaryApplicant.isSupervisor ? ` - ${copy.supervisor}` : ""
          }`}
          image={primaryApplicant.image}
          description={getApplicantDescription(
            primaryApplicant,
            copy,
            zoneOptions,
          )}
        />
      ) : (
        <Stack className="event-applicants-unassign-modal__bulk-summary">
          <Typography className="event-applicants-unassign-modal__bulk-line">
            {copy.bulkSummary
              .replace("{assigned}", assignedApplicants.length)
              .replace("{unassigned}", unassignedApplicants.length)}
          </Typography>
        </Stack>
      )}

      {shouldShowZoneClearedWarning && (
        <UICallout
          variant="error"
          className="event-applicants-unassign-modal__danger"
          icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
          title={copy.zoneWillBeCleared}
          description={copy.statusChange}
          hasBorder={false}
        />
      )}
    </UIActionModal>
  );
};

UnassignRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  applicants: PropTypes.arrayOf(PropTypes.object),
  zoneOptions: PropTypes.arrayOf(PropTypes.object),
  copy: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUnassign: PropTypes.func.isRequired,
};

UnassignRequestModal.defaultProps = {
  applicants: [],
  zoneOptions: [],
};

export default UnassignRequestModal;
